import {
  ReactNode,
  useState,
  useContext,
  createContext,
  useMemo,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';

import classNames from 'classnames';

type GlobalContainerContextStateType = {
  isAlertDisabledActive: boolean;
  isShopFilterActive: boolean;
  isScrolled: boolean;
  isUserMenuActive: boolean;
  isNoScrollActive: boolean;
  isSubmenuActive: boolean;
  isProfileDropdownActive: boolean;
  isWidgetActive: boolean;
  isLivesearchActive: boolean;
  isNoHeaderActive: boolean;
  isNoFooterActive: boolean;
  isTicketsWidgetActive: boolean;
  isMobileFiltersAccordianActive: boolean;
  isBottomOfPage: boolean;
};

type StateSetter<S> = Dispatch<SetStateAction<S>>;

type GlobalContainerContextActionType = {
  setIsShopFilterActive: StateSetter<boolean>;
  setIsScrolled: StateSetter<boolean>;
  setIsUserMenuActive: StateSetter<boolean>;
  setIsNoScrollActive: StateSetter<boolean>;
  setIsSubmenuActive: StateSetter<boolean>;
  setIsProfileDropdownActive: StateSetter<boolean>;
  setIsWidgetActive: StateSetter<boolean>;
  setIsLivesearchActive: StateSetter<boolean>;
  setIsTicketsWidgetActive: StateSetter<boolean>;
  setIsMobileFiltersAccordianActive: StateSetter<boolean>;
  setIsBottomOfPage: StateSetter<boolean>;
};

type GlobalContainerContextType = GlobalContainerContextActionType &
  GlobalContainerContextStateType;

const GlobalContainer = createContext<GlobalContainerContextType>(null);

interface GlobalContainerProviderProps {
  children: ReactNode;
  isAlertDisabledActive?: boolean;
  isNoFooterActive?: boolean;
  isNoHeaderActive?: boolean;
  isNoWidgetsActive?: boolean;
}

function GlobalContainerProvider({
  children,
  isAlertDisabledActive,
  isNoHeaderActive,
  isNoFooterActive,
  isNoWidgetsActive,
}: GlobalContainerProviderProps) {
  const [isShopFilterActive, setIsShopFilterActive] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuActive, setIsUserMenuActive] = useState(false);
  const [isNoScrollActive, setIsNoScrollActive] = useState(false);
  const [isSubmenuActive, setIsSubmenuActive] = useState(false);
  const [isProfileDropdownActive, setIsProfileDropdownActive] = useState(false);
  const [isWidgetActive, setIsWidgetActive] = useState(false);
  const [isLivesearchActive, setIsLivesearchActive] = useState(false);
  const [isTicketsWidgetActive, setIsTicketsWidgetActive] = useState(false);
  const [isMobileFiltersAccordianActive, setIsMobileFiltersAccordianActive] =
    useState(false);
  const [isBottomOfPage, setIsBottomOfPage] = useState(false);

  const contextValue: GlobalContainerContextType = useMemo(
    () => ({
      isAlertDisabledActive,
      isShopFilterActive,
      setIsShopFilterActive,
      isScrolled,
      setIsScrolled,
      isUserMenuActive,
      setIsUserMenuActive,
      isNoScrollActive,
      setIsNoScrollActive,
      isSubmenuActive,
      setIsSubmenuActive,
      isProfileDropdownActive,
      setIsProfileDropdownActive,
      isWidgetActive,
      setIsWidgetActive,
      isTicketsWidgetActive,
      setIsTicketsWidgetActive,
      isLivesearchActive,
      setIsLivesearchActive,
      isNoHeaderActive,
      isNoFooterActive,
      isNoWidgetsActive,
      isMobileFiltersAccordianActive,
      setIsMobileFiltersAccordianActive,
      isBottomOfPage,
      setIsBottomOfPage,
    }),
    [
      isAlertDisabledActive,
      isShopFilterActive,
      isScrolled,
      isUserMenuActive,
      isNoScrollActive,
      isSubmenuActive,
      isProfileDropdownActive,
      isWidgetActive,
      isTicketsWidgetActive,
      isLivesearchActive,
      isNoHeaderActive,
      isNoFooterActive,
      isNoWidgetsActive,
      isMobileFiltersAccordianActive,
      isBottomOfPage,
    ],
  );

  const className = useMemo(
    () =>
      classNames({
        disableTopBar: isAlertDisabledActive,
        shopFiltersActive: isShopFilterActive,
        'is-scrolled': isScrolled,
        activeUserMenu: isUserMenuActive,
        'submenu-active': isSubmenuActive,
        'profile-dropdown-active': isProfileDropdownActive,
        activeWidgetsSidebar: isWidgetActive,
        livesearchActive: isLivesearchActive,
        'no-header': isNoHeaderActive,
        'no-footer': isNoFooterActive,
        'no-widgets': isNoWidgetsActive,
        activeWidgetsTickets: isTicketsWidgetActive,
        mobileFiltersAccordionShow: isMobileFiltersAccordianActive,
        bottomOfPage: isBottomOfPage,
      }),
    [
      isAlertDisabledActive,
      isShopFilterActive,
      isScrolled,
      isUserMenuActive,
      isSubmenuActive,
      isProfileDropdownActive,
      isWidgetActive,
      isLivesearchActive,
      isNoHeaderActive,
      isNoFooterActive,
      isNoWidgetsActive,
      isTicketsWidgetActive,
      isMobileFiltersAccordianActive,
      isBottomOfPage,
    ],
  );

  // indicate that react has loaded and the loading screen should be removed
  useEffect(() => document.body.classList.remove('not-loaded'), []);

  // prevent scrolling
  useEffect(
    () =>
      isNoScrollActive || isWidgetActive
        ? document.body.classList.add('no-scroll')
        : document.body.classList.remove('no-scroll'),
    [isNoScrollActive, isWidgetActive],
  );

  return (
    <div id="globalContainer" className={className}>
      <GlobalContainer.Provider value={contextValue}>
        {children}
      </GlobalContainer.Provider>
    </div>
  );
}

function useGlobalContainer() {
  const context = useContext(GlobalContainer);
  if (context === undefined) {
    throw new Error('global container context is undefined');
  }

  return context;
}

export {GlobalContainerProvider, useGlobalContainer};
